import {clsx} from 'clsx'
import {twMerge} from 'tailwind-merge'
import {FILE} from '@/utils/constants/commonConstants'

export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

export const isNotEmptyAndDefined = value => {
  return value !== undefined && value !== ''
}
export const isObjectNotEmpty = obj => {
  return Object.keys(obj).every(key => obj[key] !== null && obj[key] !== undefined && obj[key] !== '')
}

/**
 * Initiates a download of a CSV file from a given CSV string.
 *
 * @param {string} csvString - The CSV data as a string.
 * @param {string} [fileName='File.csv'] - The name for the downloaded file, defaults to 'File.csv'.
 */
export const downloadCSVFiles = (csvString, fileName = 'File.csv') => {
  // Create a new Blob object using the CSV string, specifying the MIME type for CSV files.
  const blob = new Blob([csvString], {type: 'text/csv;charset=utf-8;'})

  // Create a temporary anchor element to facilitate the download.
  const link = document.createElement('a')

  // Set the download attribute with the desired file name.
  link.download = fileName

  // Create a URL for the Blob and set it as the href attribute of the link.
  link.href = URL.createObjectURL(blob)

  // Programmatically click the link to trigger the download.
  link.click()

  // Revoke the object URL to free up memory.
  URL.revokeObjectURL(link.href)
}

export const downloadInventoryCSVFiles = (csvString, fileName = 'File.csv') => {
  const rows = csvString?.split('\n')

  const numberedRows = rows?.map((row, index) => {
    if (index === 0) return row
    if (row.trim() === '') return `${index}`
    return `${index},${row}`
  })
  const numberedCSV = numberedRows.join('\n')

  const blob = new Blob([numberedCSV], {type: 'text/csv;charset=utf-8;'})
  const link = document.createElement('a')
  link.download = fileName
  link.href = URL.createObjectURL(blob)
  link.click()
  URL.revokeObjectURL(link.href)
}

export const downloadRowCsv = (blobUrl, sasToken, fileName) => {
  const downloadUrl = `${blobUrl}?${sasToken}`
  const link = document.createElement('a')
  link.href = downloadUrl
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export const downloadCsvFromCdnUrl = (cdnUrl, fileName) => {
  const link = document.createElement('a')
  link.href = cdnUrl
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export const getDateWithTimeString = () => {
  const date = new Date()
  const isoString = date.toISOString()
  const formattedDate = isoString
    .replace('T', '_')
    .replace(/\.\d{3}Z/, '')
    .replaceAll(':', '')
  return formattedDate
}

export const formatDateToMMDDYYYY = dateString => {
  if (!dateString) return '-'

  const date = new Date(dateString)

  // Check if date is valid
  if (isNaN(date.getTime())) return '-'

  const day = date.getDate().toString().padStart(2, '0')
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const year = date.getFullYear()

  return `${month}/${day}/${year}`
}

export const sanitizeFileName = fileName => {
  if (!fileName) return
  const parts = fileName?.split('.')
  const ext = parts.pop() // Get the extension
  const baseName = parts.join('.') // Join the rest (in case filename had dots)

  // Remove all non-alphanumeric characters except dash and underscore
  const cleanBaseName = baseName.replace(/[^a-zA-Z0-9-_]/g, '')
  return `${cleanBaseName}.${ext}`
}

/**
 * Validates filename for URL compatibility
 * @param {string} filename - The filename to validate
 * @returns {Object} - Object with isValid boolean and message string
 */
export const validateFilenameForUrl = filename => {
  if (!filename) return {isValid: false, message: 'Filename is required'}

  const lastDotIndex = filename.lastIndexOf('.')

  // If there's no dot, validate the entire filename for URL-safe characters
  if (lastDotIndex === -1) {
    // Check for characters that can cause URL issues (excluding spaces as they're handled by URL encoding)
    const urlUnsafeChars = /[<>:"/\\|?*]/
    if (urlUnsafeChars.test(filename)) {
      return {
        isValid: false,
        message:
          'Special characters are not allowed. Please use only letters, numbers, hyphens (-), and underscores (_).',
      }
    }
    return {isValid: true, message: ''}
  }

  const filenameWithoutExt = filename.substring(0, lastDotIndex)
  const extension = filename.substring(lastDotIndex + 1).toLowerCase()

  // Derive allowed extensions from MIME types to stay consistent with existing image validation
  const allowedExtensions = FILE.ALLOWED_IMAGE_TYPES.map(mimeType => {
    const ext = mimeType.split('/')[1]
    return ext === 'jpeg' ? ['jpg', 'jpeg'] : [ext]
  }).flat()

  // If extension is not in allowed list, treat as invalid (dot in filename)
  if (!allowedExtensions.includes(extension)) {
    return {
      isValid: false,
      message: 'Dots (.) in filename are not supported. Please rename your file to remove dots from the filename.',
    }
  }

  // Check if filename contains dots (other than the extension dot)
  if (filenameWithoutExt.includes('.')) {
    return {
      isValid: false,
      message: 'Dots (.) in filename are not supported. Please rename your file to remove dots from the filename.',
    }
  }

  // Check for characters that can cause URL issues in the filename part (excluding spaces)
  const urlUnsafeChars = /[<>:"/\\|?*]/
  if (urlUnsafeChars.test(filenameWithoutExt)) {
    return {
      isValid: false,
      message:
        'Special characters are not allowed. Please use only letters, numbers, hyphens (-), and underscores (_).',
    }
  }

  return {isValid: true, message: ''}
}

export const convertImageToWebP = async (file, options = {}) => {
  const {quality = 0.9, entityType = null} = options
  const fileName = file?.name?.replace(/\.(png|jpe?g)$/i, '')

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = event => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0)

        canvas.toBlob(
          blob => {
            if (blob) {
              const webpFile = new File([blob], file?.type === 'image/webp' ? fileName : `${fileName}.webp`, {
                type: 'image/webp',
              })
              webpFile.error = file?.error ?? false

              resolve({
                webpFile,
                metadata: {
                  originalFileName: file?.name,
                  id: file?.type === 'image/webp' ? fileName?.split('.')[0] : fileName,
                  blobFormat: 'webp',
                  width: img.width,
                  height: img.height,
                  originalFormat: file?.type,
                  originalSize: file?.size,
                  blobFileSize: webpFile.size,
                  referenceEntity: entityType,
                },
                file: file,
              })
            } else {
              reject(new Error('Conversion to WebP failed'))
            }
          },
          'image/webp',
          quality
        )
      }
      img.onerror = () => reject(new Error('Image load failed'))
      img.src = event.target.result
    }
    reader.onerror = () => reject(new Error('File reading failed'))
    reader.readAsDataURL(file)
  })
}
export const normalizeSubCategories = value => {
  if (
    typeof value === 'object' &&
    value !== null &&
    value?.subCategoryId !== null &&
    value?.subCategoryId !== undefined
  ) {
    return [
      {
        code: value?.subCategoryId,
        values: value?.values || {},
      },
    ]
  }
  return []
}
export const extractLanguageValues = (arr, lang) => {
  if (!Array.isArray(arr)) return arr
  return arr
    .map(item => item?.values?.[lang])
    .filter(Boolean)
    .join(',')
}
